import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import i18n from '../../../Config/app/i18n';
import errorMapper, { Error } from '../../utils/errorMapper';

const DEFAULT_FORM_VALUES = {
  username: '',
  password1: '',
  password2: '',
  tos_consent: true,
  pp_consent: true,
  marketing_consent: true,
  recaptcha_response_v3: '',
};

const RegisterValidator = z
  .object({
    username: z.string().email(),
    password1: z.string().min(1, i18n.t('validation:password_required')),
    password2: z.string().min(1, i18n.t('validation:password_required')),
    tos_consent: z.boolean(),
    pp_consent: z.boolean(),
    marketing_consent: z.boolean(),
    recaptcha_response_v3: z.string(),
    recaptcha_response_v2: z.string().optional(),
  })
  .refine((data) => data.password1 === data.password2, {
    message: i18n.t('validation:match_password'),
    path: ['password2'],
  });

export type RegisterForm = z.infer<typeof RegisterValidator>;

export const useRegister = () => {
  const { track } = useAnalytics();
  const [useV2, setUseV2] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm<RegisterForm>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(RegisterValidator),
  });

  const mutation = useMutation<{ email: string }, Error<RegisterForm>, RegisterForm>({
    mutationKey: ['accounts'],
    onSuccess: () => {
      track(
        'register_form_success',
        { event_category: 'User Registration', event_label: 'register_form' },
        () => window.location.reload(),
      );
    },
    onError: (error) => {
      track('register_form_error', {
        event_category: 'User Registration',
        event_label: 'register_form',
      });

      errorMapper(error.response?.data?.errors || {}, (field, message) => {
        if (field === 'recaptcha_response_v3') {
          setUseV2(true);
        }

        setError(field, { message });
      });
    },
  });

  const onSubmit = (data: RegisterForm) => {
    track('register_form_submit', {
      event_category: 'User Registration',
      event_label: 'register_form',
    });

    mutation.mutate(data);
  };

  const onVerify = useCallback(
    (token: string) => {
      setValue('recaptcha_response_v3', token);
    },
    [mutation.submittedAt],
  );

  register('tos_consent');
  register('pp_consent');
  register('marketing_consent');
  register('recaptcha_response_v3', { required: true });

  return {
    control,
    useV2,
    register,
    handleSubmit,
    onSubmit,
    errors,
    onVerify,
    isPending: mutation.isPending,
  };
};
